import { PaymentType } from '@/types';
import type { EvaluateShippingRulesRequest, Order } from '@/types';

import { normalizeProvinceCode } from './provinceCode';

/**
 * RS-22 — map an order draft to the evaluate-shipping-rules request body.
 *
 * RC-1 (2026-05-05): backend rule conditions are persisted as
 * `province_code` strings (e.g. 'BG', 'BGD'). The original implementation
 * passed the contact's `region_uid` (a ULID) as `destination`, which never
 * matched any rule, silently disabling the entire feature.
 *
 * 2026-05-12: per merchant directive, the dashboard now emits the
 * canonical 2-letter province form ('BG' rather than 'BGD') so backend
 * comparisons against carrier supported_provinces don't drift across
 * formats. The 3-letter codes still living in regions.province_code are
 * normalised via `normalizeProvinceCode` before transmission. The backend
 * `ProvinceCodeNormalizer` accepts either form, so this change is
 * defensive on both sides.
 *
 *   - source: pickup address's `region.province_code` (normalised)
 *   - destination: the picker-selected province_code (normalised)
 *   - package size is upper-cased ('s' → 'S')
 *   - cod_amount is ORDER PRODUCTS VALUE (sum of price × quantity), NOT
 *     fee_customer_payable. This matches the backend's
 *     OrderRuleAssignmentService::buildEvaluatorInput so preview and
 *     order-creation use the same number (BUG-SR-005 / -006 / -009,
 *     May 2026 QA — fee_customer_payable includes shipping fee /
 *     prepayment / discount and caused the lt/lte rule to fire wrongly
 *     while gt/gte rules silently missed).
 *   - cod_amount omitted for PREPAID — paid in advance, no rider COD.
 *
 * Any missing-but-meaningful field is left undefined rather than '' / 0
 * so the backend's default-skipping behaviour kicks in.
 */
export function buildEvaluateShippingRulesRequest(
  order: Order,
  sourceProvinceCode: string,
  destinationProvinceCode: string,
): EvaluateShippingRulesRequest {
  const isCOD = order.payment_type === PaymentType.COLLECT_ON_DELIVERY;
  const size = order.size ? String(order.size).toUpperCase() : undefined;

  return {
    source: normalizeProvinceCode(sourceProvinceCode),
    destination: normalizeProvinceCode(destinationProvinceCode),
    package_size: size as EvaluateShippingRulesRequest['package_size'],
    order_type: (order.type as EvaluateShippingRulesRequest['order_type']) ?? 'DEFAULT',
    fragile: !!order.is_fragile,
    // TEMP-9 — cold-chain flag; matches the backend evaluator input
    // (OrderRuleAssignmentService::buildEvaluatorInput temperature_control).
    temperature_control: !!order.temperature_control,
    qlue: !!order.clue_verification,
    cod_amount: isCOD ? computeProductsValue(order) : undefined,
  };
}

/**
 * Products subtotal = sum(price × quantity). Mirrors backend
 * OrderRuleAssignmentService::buildEvaluatorInput so preview/eval and
 * order-creation agree on the rule input. Tolerates string price/qty
 * (form inputs are strings before normalisation) and missing quantity
 * (defaults to 1, matching the backend default).
 */
function computeProductsValue(order: Order): number {
  const items = order.products ?? [];
  let total = 0;
  for (const p of items) {
    const price = Number(p?.price ?? 0) || 0;
    const qtyRaw = p?.quantity;
    const qty = qtyRaw === undefined || qtyRaw === null || qtyRaw === ''
      ? 1
      : Number(qtyRaw) || 0;
    total += price * qty;
  }
  return total;
}
